import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faUserGraduate,
  faUsersGear,
  faGear,
  faPen,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import swal from "sweetalert";
import { APIMS } from "../../../../../config/apims";
import { APIUS } from "../../../../../config/apius";
import GenerateStudentModal from "./modal/generate";
import Pagination from "../../../../components/pagination_new";
import "bulma/css/bulma.css";
import "../../../../../index.css";

const API_LIMIT = 1000;
const text = (value) => (value == null ? "" : String(value));

// null means the field has not yet been included in the student response.
const STUDENT_DETAIL_TABS = [
  { 
    title: "Data Pribadi", 
    sections: [
      { 
        title: "Identitas Siswa", 
        fields: [
          ["Nama lengkap", "nama_lengkap"], 
          ["Panggilan", "nama_alias"], 
          ["Tahun ajaran", "tahun_ajaran"],
          ["NIS", "nis_ypi"], 
          ["NISN", "nisn"], 
          ["Jenis Kelamin", "jenis_kelamin"],
          ["Tempat lahir", "tempat_lahir"], 
          ["Tanggal lahir", "tanggal_lahir_siswa"],
          ["Agama", "agama"], 
          ["Kewarganegaraan", "kewarnegaraan"], 
          ["Anak ke", "anak_ke"],
          ["NIK", "nik"], 
          ["No. akte kelahiran", null], 
          ["No. KK", "no_kk"], 
          ["Email sekolah", ["email_siswa", "email_murid"]], 
          ["Email belajar.id", null],
          ["Jumlah saudara kandung", "jumlah_saudara_kandung"], 
          ["Jumlah saudara tiri", null],
          ["Status anak", null], 
          ["Bahasa di rumah", "bahasa_dirumah"], 
          ["Golongan darah", null],
          ["Lingkar kepala", "linkar_kepala"],
          ["Berat badan", "berat"], 
          ["Tinggi badan", "tinggi"],
        ]
      }
    ]
  },
  { 
    title: "Alamat", 
    sections: [
      { 
        title: "Alamat di KK", 
        fields: [
          ["Alamat", "alamat_lengkap"], 
          ["RT", null], ["RW", null], 
          ["Desa/Kelurahan", "kelurahan"],
          ["Kecamatan", "kecamatan"], 
          ["Kode pos", "kode_pos"], 
          ["Kabupaten/Kota", "kota"], 
          ["Provinsi", "provinsi"],
        ] 
      },
      { 
        title: "Alamat Saat Ini", 
        fields: [
          ["Alamat", null], 
          ["Kelurahan", null], 
          ["Kecamatan", null], 
          ["Kabupaten/Kota", null],
          ["Kode pos", null], 
          ["Tinggal bersama", null], 
          ["Jarak ke sekolah", "jarak_rumah_sekolah"],
        ]
      },
    ] 
  },
  { 
    title: "Pendidikan", 
    sections: [
      { 
        title: "Pendidikan Sebelumnya", 
        fields: [
          ["Sekolah asal", "sekolah_asal"], 
          ["Tanggal ijazah", null], 
          ["No. ijazah", null], 
          ["Lama belajar", null]
        ] 
      },
      { 
        title: "Pindahan/Mutasi Masuk", 
        fields: [
          ["Dari sekolah", null], 
          ["Alasan", null]
        ] 
      },
      { 
        title: "Status Murid", 
        fields: [
          ["Status murid", null]
        ] 
      },
      { 
        title: "Diterima di Sekolah Ini", 
        fields: [
          ["Di kelas", "name_grade"], 
          ["Tanggal", null]
        ] 
      },
    ] 
  },
  { 
    title: "Informasi Orang Tua", 
    sections: [
      { 
        title: "Ayah", 
        fields: [
          ["Nama", "nama_lengkap_ayah"], 
          ["Tanggal lahir", "tanggal_lahir_ayah"], 
          ["Pendidikan terakhir", "pendidikan_terakhir_ayah"], 
          ["Pekerjaan", "pekerjaan_ayah"], 
          ["Penghasilan", "penghasilan_ayah"], 
          ["NIK", "nik_ayah"], 
          ["Kontak", "no_handphone_ayah"], 
          ["Keadaan", "keterangan_ayah"]
        ] 
      },
      { 
        title: "Ibu", 
        fields: [
          ["Nama", "nama_lengkap_ibu"], 
          ["Tanggal lahir", "tanggal_lahir_ibu"], 
          ["Pendidikan terakhir", null], 
          ["Pekerjaan", "pekerjaan_ibu"], 
          ["Penghasilan", "penghasilan_ibu"], 
          ["NIK", "nik_ibu"], 
          ["Kontak", "no_handphone_ibu"], 
          ["Keadaan", "keterangan_ibu"]
        ] 
      },
    ] 
  },
  { 
    title: "Perkembangan Peserta Didik", 
    sections: [
      { 
        title: "Perkembangan Peserta Didik", 
        fields: [
          ["Beasiswa", null], 
          ["Pindah sekolah (tanggal)", null], 
          ["Alasan", null], 
          ["Sekolah tujuan", null]
        ] 
      }
    ] 
  },
];

const detailValue = (student, field) => {
  if (!field) return "Belum tersedia";
  
  const keys = Array.isArray(field) ? field : [field];
  
  const value = keys.map((key) => student?.[key]).find((item) => item !== null && item !== undefined && String(item).trim() !== "");

  return value === undefined ? "Belum tersedia" : String(value);
};

function StudentDetailModal({ student, onClose }) {
  const [activeTab, setActiveTab] = useState(0);
  
  useEffect(() => {
    if (!student) return undefined;
    const onEscape = (event) => { if (event.key === "Escape") onClose(); };
  
    document.addEventListener("keydown", onEscape);
  
    return () => document.removeEventListener("keydown", onEscape);
  }, [student, onClose]);
  
  if (!student) return null;

  return (
    <div 
      className="student-detail-overlay" 
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div 
        className="student-detail-modal" 
        role="dialog" 
        aria-modal="true" 
        aria-label={`Detail siswa ${student.nama_lengkap || ""}`}
      >
        <div className="student-detail-header">
          <div>
            <h5>Detail Siswa</h5>
            <p>{student.nama_lengkap || "-"} · NISN {student.nisn || "-"}</p>
          </div>

          <button 
            type="button" 
            className="student-detail-close" 
            onClick={onClose} 
            aria-label="Tutup detail">
              ×
          </button>
        </div>

        <div 
          className="student-detail-tabs" 
          role="tablist" 
          aria-label="Jenis informasi siswa"
        >
          {STUDENT_DETAIL_TABS.map((tab, index) => (
            <button 
            key={tab.title} 
            type="button" 
            role="tab" 
            aria-selected={activeTab === index} 
            className={activeTab === index ? "is-active" : ""} 
            onClick={() => setActiveTab(index)}
            >
              {tab.title}
            </button>
          ))}
        </div>

        <div className="student-detail-body" role="tabpanel">
          {STUDENT_DETAIL_TABS[activeTab].sections.map((section) => (
            <section 
              key={section.title} 
              className="student-detail-section"
            >
              <h6>{section.title}</h6>
              <div className="student-detail-grid">
                {section.fields.map(([label, field]) => {
                  const value = detailValue(student, field);
                  const unavailable = value === "Belum tersedia";
                  return <div className="student-detail-field" key={label}>
                    <span>{label}</span>
                    <strong 
                      className={unavailable ? "student-detail-unavailable" : undefined}
                    >
                      {value}
                    </strong>
                  </div>;
                })}
              </div>
            </section>
          ))}
        </div>
        <div className="student-detail-footer">
          <button type="button" onClick={onClose}>Tutup</button>
        </div>
      </div>
    </div>
  );
}

function StudentStatusModal({ student, onClose, onSaved, headers }) {
  const [active, setActive] = useState(Number(student?.state_form) === 1);
  const [date, setDate] = useState(() => {
    const savedDate = student?.tanggal_nonaktif || student?.inactive_date || student?.deactivated_at;
    return savedDate ? String(savedDate).slice(0, 10) : Number(student?.state_form) === 1
      ? new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10)
      : "";
  });
  const [reason, setReason] = useState(student?.keterangan_nonaktif || student?.inactive_reason || "");
  const [saving, setSaving] = useState(false);
  if (!student) return null;

  const save = async (event) => {
    event.preventDefault();
    if (active === (Number(student.state_form) === 1)) return onClose();
    if (!active && (!date || !reason.trim())) return;
    // ID ini berasal langsung dari setiap baris respons POST /api/student-admitted.
    const studentId = student?.id;
    if (!studentId) {
      swal("Gagal", "ID siswa tidak ada pada respons tabel.", "error");
      return;
    }
    setSaving(true);
    try {
      if (active) {
        await APIUS.put(`/api/student-admitted/activate/${encodeURIComponent(studentId)}`, { user_id: studentId }, headers);
      } else {
        await APIUS.delete(`/api/student-admitted/${encodeURIComponent(studentId)}`, {
          ...headers,
          data: {
            tanggal_nonaktif: date,
            keterangan_nonaktif: reason.trim(),
          },
        });
      }
      onSaved();
      onClose();
      swal("Berhasil", active ? "Status siswa berhasil diaktifkan." : "Status siswa berhasil dinonaktifkan.", "success");
    } catch (error) {
      swal("Gagal", error?.response?.data?.message || "Gagal mengubah status siswa.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="student-detail-overlay" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <form className="student-status-modal" onSubmit={save} role="dialog" aria-modal="true" aria-label="Status Siswa">
        <div className="student-status-head"><h3>Status Siswa</h3><button type="button" onClick={onClose} disabled={saving} aria-label="Tutup">×</button></div>
        <div className="student-status-body">
          <div className="student-status-info">
            <div className="row">
              <div className="col-6">
                <strong>
                  <div className="mb-2" style={{fontWeight: 700, color: "#3f5bea"}}>Nama Siswa</div>
                  <div style={{fontWeight: 700, fontSize: "15px"}}> {student?.nama_lengkap || student?.full_name || "-"}</div>
                  
                </strong>
              </div>
              <div className="col-6">
                <strong>
                  <div className="mb-2" style={{fontWeight: 700, color: "#3f5bea"}}>NISN</div>
                  <div style={{fontWeight: 700, fontSize: "15px"}}>{student?.nisn || "-"}</div>
                </strong>
              </div>
            </div>
          </div>
          <div className="student-status-switch-row"><strong>Status Siswa</strong><label className="student-status-switch-control"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={saving}/><span className="student-status-switch"/><span className={active ? "active-label" : "inactive-label"}>{active ? "Aktif" : "Tidak Aktif"}</span></label></div>
          {!active && <>
            <label className="student-status-field">Tanggal<input type="date" value={date} onChange={(e) => setDate(e.target.value)} required disabled={saving}/></label>
            <label className="student-status-field">Keterangan <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Masukkan alasan siswa dinonaktifkan" required disabled={saving}/></label>
          </>}
        </div>
        <div className="student-status-footer"><button type="button" onClick={onClose} disabled={saving}>Batal</button><button type="submit" disabled={saving}>{saving ? "Menyimpan..." : "▣  Simpan"}</button></div>
      </form>
    </div>
  );
}

export default function Siswa() {
  document.title = "Siswa";

  const [getData, setGetData] = useState([]);

  const token = localStorage.getItem("token");
  const storageBranch = localStorage.getItem("ysb_branch_id");
  const storageLevel = localStorage.getItem("level");

  const [isFetching, setIsFetching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFilterDirty, setIsFilterDirty] = useState(false);

  const [permission, setPermission] = useState({ create: 0, update: 0 });

  const [loading, setLoading] = useState(true);
  const [loadingSchool, setLoadingSchool] = useState(false);

  const [modalGenerate, setModalGenerate] = useState(false);
  const [detailStudent, setDetailStudent] = useState(null);
  const [statusStudent, setStatusStudent] = useState(null);

  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [semesters, setSemesters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [schools, setSchools] = useState([]);
  
  const [rows, setRows] = useState([]);
  const firstLoadDone = useRef(false);

  // mencegah respons API lama mengubah data setelah pindah filter atau meninggalkan halaman.
  const schoolRequestRef = useRef(0);
  const tableRequestRef = useRef(0);

  useEffect(() => () => {
    ++schoolRequestRef.current;
    ++tableRequestRef.current;
  }, []);

  const [academicYear, setAcademicYear] = useState("");

  const [search, setSearch] = useState("");

  const [filters, setFilters] = useState({
    ysb_branch_id: "",
    ysb_school_id: "",
  });

  const location = useLocation();
  const navigate = useNavigate();
  const headers = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token],
  );

  useEffect(() => {
    if (["developer", "superadmin"].includes(storageLevel)) {
      setPermission({ create: 1, update: 1 });
      return;
    }
    const access = (
      JSON.parse(localStorage.getItem("role_permission")) || []
    ).find((item) => item.menu?.url === location.pathname);
    if (!access) navigate("/dashboard");
    else setPermission({ create: access.create, update: access.update });
  }, [location.pathname, navigate, storageLevel]);

  const yearOptions = useMemo(
    () => [
      ...new Set(semesters.map((item) => item?.name_year).filter(Boolean)),
    ],
    [semesters],
  );
  const academicYearId = useMemo(
    () => semesters.find((item) => item?.name_year === academicYear)?.ysb_academic_year_id,
    [academicYear, semesters],
  );
  const activeSemester = useMemo(
    () => semesters.find((item) => Number(item?.status) === 1) || semesters[0],
    [semesters],
  );

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      try {
        const [branchRes, semesterRes] = await Promise.all([
          APIMS.get(
            `/api/access/branches?page=1&limit=${API_LIMIT}&ascending=1&search=&branch=${storageBranch}&level=${storageLevel}`,
            headers,
          ),
          APIMS.get(
            `/api/access/semesters?page=1&limit=${API_LIMIT}&ascending=0&search=`,
            headers,
          ),
        ]);
        const branchData = branchRes?.data?.data || [];
        const semesterData = semesterRes?.data?.data || [];
        const active =
          semesterData.find((item) => Number(item?.status) === 1) ||
          semesterData[0];
        setBranches(branchData);
        setSemesters(semesterData);
        setAcademicYear(active?.name_year || "");
        setFilters((old) => ({
          ...old,
          ysb_branch_id: branchData[0]?.branch_code || "",
        }));
      } catch (error) {
        swal({
          title: "Gagal",
          text:
            error?.response?.data?.message || "Gagal memuat filter siswa",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    loadOptions();
  }, [headers, storageBranch, storageLevel]);

  useEffect(() => {
    if (!filters.ysb_branch_id) return;
    const requestId = ++schoolRequestRef.current;
    const loadSchools = async () => {
      setLoadingSchool(true);
      try {
        const response = await APIMS.get(
          `/api/access/schools?page=1&limit=${API_LIMIT}&ascending=1&branch=${filters.ysb_branch_id}&level=${storageLevel}`,
          headers,
        );
        if (requestId !== schoolRequestRef.current) return;
        let data = response?.data?.data || [];
        if (["developer", "superadmin"].includes(storageLevel))
          data = data.filter(
            (item) => item?.ysb_branch_id === filters.ysb_branch_id,
          );
        if (filters.ysb_branch_id === "BKS")
          data = data.filter((item) => item?.school_code !== "BASEMENT_BEKASI");
        data.sort((a, b) =>
          text(a?.school_name).localeCompare(text(b?.school_name), "id", {
            numeric: true,
          }),
        );
        setSchools(data);
        setFilters((old) => ({
          ...old,
          ysb_school_id: data[0]?.school_code || "",
        }));
      } catch {
        if (requestId !== schoolRequestRef.current) return;
        setSchools([]);
        setFilters((old) => ({ ...old, ysb_school_id: "" }));
      } finally {
        if (requestId === schoolRequestRef.current) setLoadingSchool(false);
      }
    };
    loadSchools();
    return () => { ++schoolRequestRef.current; };
  }, [filters.ysb_branch_id, headers, storageLevel]);

  const fetchStudents = useCallback(async () => {
    if (
      !academicYearId ||
      !filters.ysb_branch_id ||
      !filters.ysb_school_id || loadingSchool
    ) {
      return;
    }

    const requestId = ++tableRequestRef.current;
    setIsFetching(true);
    setSearch("");
    setTablePage(1);
    try {
      const response = await APIUS.post(
        "/api/student-admitted",
        {
          ysb_academic_year_id: academicYearId,
          ysb_branch_id: filters.ysb_branch_id,
          ysb_school_id: filters.ysb_school_id,
        },
        headers,
      );

      if (requestId !== tableRequestRef.current) return;
      setRows((response?.data?.data || []).map((item) => ({
          ...item,
          full_name: item?.nama_lengkap || "-",
          email: item?.email_siswa || item?.email_murid || "-",
          photo: item?.foto_siswa || "",
      })));
      setHasSearched(true);
      setIsFilterDirty(false);
    } catch (error) {
      if (requestId !== tableRequestRef.current) return;
      setRows([]);
      setHasSearched(false);
      setIsFilterDirty(true);
      swal({
        title: "Gagal",
        text: error?.response?.data?.message || "Gagal mengambil data siswa",
        icon: "error",
      });
    } finally {
      if (requestId === tableRequestRef.current) setIsFetching(false);
    }
  }, [academicYearId, filters.ysb_branch_id, filters.ysb_school_id, headers, loadingSchool]);

  const isFilterComplete = !!academicYearId && !!filters.ysb_branch_id &&
    !!filters.ysb_school_id && !loadingSchool;

  useEffect(() => {
    if (firstLoadDone.current || isFilterDirty || loading || !isFilterComplete) return;
    firstLoadDone.current = true;
    fetchStudents();
  }, [fetchStudents, isFilterDirty, loading, isFilterComplete]);

  const invalidateFilter = () => {
    ++tableRequestRef.current;
    setRows([]);
    setSearch("");
    setTablePage(1);
    setIsFetching(false);
    setIsFilterDirty(true);
    setHasSearched(false);
  };

  const changeFilter = (event) => {
    const { name, value } = event.target;
    if (name === "ysb_branch_id") {
      ++schoolRequestRef.current;
      setSchools([]);
      setLoadingSchool(!!value);
    }
    setFilters((old) => ({
      ...old,
      [name]: value,
      ...(name === "ysb_branch_id" ? { ysb_school_id: "" } : {}),
    }));
    invalidateFilter();
  };

  const changeYear = (event) => {
    setAcademicYear(event.target.value);
    invalidateFilter();
  };

  const handleCari = async () => {
    if (loadingSchool) {
      swal({ title: "Mohon tunggu", text: "Data sekolah sedang dimuat.", icon: "info" });
      return;
    }
    if (!isFilterComplete) {
      swal({ title: "Filter belum lengkap", text: "Pastikan Tahun Ajaran, Cabang, dan Sekolah sudah dipilih.", icon: "warning" });
      return;
    }
    if (isFetching) return;
    await fetchStudents();
  };

  const handleGenerateSuccess = () => {
    setTablePage(1);
    fetchStudents();
  };

    const nameCollator = useMemo(
    () => new Intl.Collator("id", { sensitivity: "base", numeric: true }),
    []
  );
   
  const filteredData = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const result = !keyword
      ? rows
      : rows.filter((item) =>
          [item?.nisn, item?.full_name, item?.email].some((value) =>
            text(value).toLowerCase().includes(keyword)
          )
        );

    return [...result].sort((a, b) =>
      nameCollator.compare(
        String(a?.full_name || "").trim(),
        String(b?.full_name || "").trim()
      )
    );
  }, [rows, search, nameCollator]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  useEffect(() => {
    setTablePage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const paginatedData = useMemo(() => {
    const safeRows = Math.max(1, rowsPerPage);
    const start = (tablePage - 1) * safeRows;
    return filteredData.slice(start, start + safeRows);
  }, [filteredData, tablePage, rowsPerPage]);

  useEffect(() => {
    setTablePage(1);
  }, [search, academicYear, filters.ysb_branch_id, filters.ysb_school_id]);

  const safeStr = (value) =>
    value === null || value === undefined ? "" : String(value);

  const toTitleCase = (value) => {
    const text = safeStr(value).toLowerCase().trim();

    if (!text) {
      return "";
    }

    return text.replace(/\w\S*/g, (word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
  };
  return (
    <div className="body sims-page">
      <StudentDetailModal student={detailStudent} onClose={() => setDetailStudent(null)} />
      {statusStudent && <StudentStatusModal key={statusStudent.id} student={statusStudent} onClose={() => setStatusStudent(null)} onSaved={fetchStudents} headers={headers} />}
      <GenerateStudentModal
        show={modalGenerate}
        onClose={() => setModalGenerate(false)}
        onSuccess={handleGenerateSuccess}
        semesters={semesters}
        branches={branches}
        activeSemester={activeSemester}
        defaultBranchId={filters.ysb_branch_id}
        defaultSchoolId={filters.ysb_school_id}
        headers={headers}
        storageLevel={storageLevel}
      />

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faUserGraduate} />
            </span>
            <h4 className="page-title-text"> Data Siswa</h4>
          </div>

          <div className="ml-auto menu-navigation-wrapper">
            <button
              type="button"
              className="btn btn-create sims-header-button"
              onClick={() => setModalGenerate(true)}
              disabled={!permission.create || loading}
            >
              <FontAwesomeIcon icon={faUsersGear} />
              <span> Generate Siswa</span>
            </button>

            <Link
              to="/data-siswa/config-siswa"
              className="menu-navigation-button"
            >
              <FontAwesomeIcon icon={faGear} />
              <span className="ml-1"> Konfigurasi Siswa</span>
            </Link>
          </div>

        </div>

        
      </div>

      <div className="body-content sims-content">
        {loading ? (
          <div className="sims-filter-card sims-filter-card-two-shimmer">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="shimmer sims-filter-skeleton" />
            ))}
          </div>
        ) : (
          <>
            <div className="sims-filter-card sims-filter-card-two">
              <div className="sims-filter-field">
                <label className="sims-filter-label">Tahun Ajaran</label>
                <select
                  className="sims-filter-select"
                  value={academicYear}
                  onChange={changeYear}
                >
                  <option value="" disabled>
                    Pilih Tahun Ajaran
                  </option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}{year === activeSemester?.name_year ? " • Aktif" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sims-filter-field">
                <label className="sims-filter-label">Cabang</label>
                <select
                  className="sims-filter-select"
                  name="ysb_branch_id"
                  value={filters.ysb_branch_id}
                  onChange={changeFilter}
                >
                  <option value="" disabled>
                    Pilih Cabang
                  </option>
                  {branches.map((item) => (
                    <option key={item?.branch_code} value={item?.branch_code}>
                      {item?.branch_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sims-filter-field">
                <label className="sims-filter-label">Sekolah</label>
                <select
                  className="sims-filter-select"
                  name="ysb_school_id"
                  value={filters.ysb_school_id}
                  onChange={changeFilter}
                  disabled={loadingSchool || !filters.ysb_branch_id}
                >
                  <option value="" disabled>
                    {loadingSchool ? "Memuat data sekolah..." : "Pilih Sekolah"}
                  </option>
                  {schools.map((item) => (
                    <option key={item?.school_code} value={item?.school_code}>
                      {item?.school_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sims-filter-action">
                  <button
                    type="button"
                    className="sims-filter-button"
                    disabled={!isFilterComplete || isFetching || (hasSearched && !isFilterDirty)}
                    onClick={handleCari}
                  >
                    Tampilkan Data
                  </button>
              </div>
            </div>
          </>
          
        )}

        {isFetching && (
          <div className="sims-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        )}

        {!loading && !loadingSchool && isFilterDirty && !isFetching && !hasSearched && (
          <div className="sims-empty-state">
            Silakan klik <strong>Tampilkan Data</strong> untuk menampilkan data sesuai filter.
          </div>
        )}

        {hasSearched && !isFetching && (
          <>

            <div className="sims-search-input-two">
              <div className="sims-search-two">
                <div className="sims-search-wrap">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="sims-search-icon"
                  />
                  <input
                    type="text"
                    className="sims-search-input"
                    placeholder="Cari siswa"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
              </div>

            </div>

            <div className="sims-table-tab-card ">              
              <div className="sims-table-tab-wrap">
                <table className="sims-table-tab">
                  <thead>
                    <tr>
                      <th className="sims-col-number">No</th>
                      
                      <th>Nisn</th>
                      <th>Nama</th>
                      <th>Status</th>
                      <th>Keterangan</th>
                      <th className="text-center">Aksi</th>
                      
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="sims-empty-state">
                          <div className="sims-empty-state-content">
                            Data siswa tidak ditemukan
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((item, index) => (
                        <tr
                          key={`${item?.source_student_id ?? item?.id ?? "siswa"}-${item?.ysb_semester_id ?? "semester"}-${(tablePage - 1) * rowsPerPage + index}`}
                        >
                          <td className="sims-col-number">
                            <span className="sims-number-badge">
                              {(tablePage - 1) * rowsPerPage + index + 1}
                            </span>
                          </td>

                          <td>
                            <strong>{item?.nisn || "-"}</strong>
                          </td>
                          <td>
                            <strong>{toTitleCase(item?.full_name || "-")}</strong>
                          </td>
                          <td>
                            <button type="button" onClick={() => setStatusStudent(item)} title="Ubah status siswa" aria-label={`Ubah status ${item?.nama_lengkap || item?.full_name || "siswa"}`}
                              className={`student-status-badge ${Number(item?.state_form) === 1 ? "is-active" : "is-inactive"}`}
                            >
                              {Number(item?.state_form) === 1 ? "Aktif" : "Tidak Aktif"}
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                          </td>
                          <td>
                            {item?.keterangan_nonaktif }
                          </td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="sims-table-button"
                              onClick={() => setDetailStudent(item)}
                              title="Detail"
                            >
                              Detail
                            </button>
                          </td>


                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                totalRows={filteredData.length}
                tablePage={tablePage}
                rowsPerPage={rowsPerPage}
                onPageChange={setTablePage}
                onRowsPerPageChange={(value) => {
                  setRowsPerPage(value);
                  setTablePage(1);
                }}
              />
            </div>
          </>
        )}
      </div>
      <style>{`
        .student-detail-overlay { 
          position: fixed; 
          inset: 0; 
          z-index: 1200; 
          background: rgba(15, 23, 42, .55); 
          display: flex;
          justify-content: center; 
          align-items: center; 
          padding: 20px; 
        }
        .student-detail-modal { 
          width: min(100%, 1050px); 
          height: min(80vh, 720px); 
          max-height: 90vh; 
          display: flex; 
          flex-direction: column; 
          background: #fff; 
          border-radius: 16px; 
          overflow: hidden; 
          box-shadow: 0 18px 55px rgba(15, 23, 42, .22); 
          font-size: 13px; 
        }
        .student-detail-header { 
          display: flex; 
          justify-content: space-between; 
          align-items: flex-start; 
          padding: 20px 24px 15px; 
          background: #f3f8fd; 
          border-bottom: 1px solid #dbe7f3; 
        }
        .student-detail-header h5 { 
          margin: 0 0 4px; 
          font-size: 19px; 
          font-weight: 700; 
          // color: #3f5bea; 
        }
        .student-detail-header p { 
          margin: 0; 
          color: #64748b; 
        }
        .student-detail-close { 
          border: 0; 
          background: transparent; 
          color: #475569; 
          font-size: 26px; 
          cursor: pointer; 
          line-height: 1; 
        }
        .student-detail-tabs { 
          display: flex; 
          gap: 4px; 
          padding: 10px 18px 0; 
          overflow-x: auto; 
          flex-shrink: 0; 
          border-bottom: 1px solid #dbe7f3; 
        }
        .student-detail-tabs button { 
          background: transparent; 
          border: 0; 
          border-bottom: 3px solid transparent; 
          padding: 11px 13px; 
          white-space: nowrap; 
          color: #64748b; 
          font-size: 13px; 
          font-weight: 600; 
          cursor: pointer; 
        }
        .student-detail-tabs button.is-active { 
          color: #3f5bea; 
          border-bottom-color: #3f5bea; 
        }
        .student-detail-body { 
          flex: 1; 
          min-height: 0; 
          padding: 22px 24px 32px; 
          overflow-y: auto; 
          background: #f8fbff; 
        }
        .student-detail-intro { 
          display: flex; 
          align-items: center; 
          justify-content: space-between; 
          gap: 12px; 
          padding: 18px 20px; 
          margin-bottom: 20px; 
          border: 1px solid #d8e8fa; 
          border-radius: 12px; 
          background: linear-gradient(110deg, #eaf4ff, #fff); 
        }
        .student-detail-intro span:first-child { 
          color: #5984ae; 
          font-size: 10px; 
          letter-spacing: .1em; 
          font-weight: 700; 
        }
        .student-detail-intro h6 { 
          margin: 4px 0 0; 
          color: #3f5bea; 
          font-size: 17px; 
          font-weight: 700; 
        }
        .student-detail-count { 
          padding: 6px 10px; 
          border-radius: 999px; 
          background: #fff; 
          border: 1px solid #cfe3f7; 
          color: #21629d; 
          white-space: nowrap; 
          font-weight: 600; 
        }
        .student-detail-section + .student-detail-section { 
          margin-top: 24px; 
        }
        .student-detail-section h6 { 
          font-size: 14px; 
          font-weight: 700; 
          color: #3f5bea; 
          margin: 0 0 12px; 
          padding-bottom: 8px; 
          border-bottom: 1px solid #e2e8f0; 
        }
        .student-detail-grid { 
          display: grid; 
          grid-template-columns: repeat(3, minmax(0, 1fr)); 
          gap: 12px; 
        }
        .student-detail-field { 
          background: #fff; 
          border: 1px solid #dce7f3; 
          border-radius: 9px; 
          padding: 12px 14px; 
          min-width: 0; 
          box-shadow: 0 2px 7px rgba(18, 67, 111, .035); 
        }
        .student-detail-field span { 
          display: block; 
          color: #64748b; 
          margin-bottom: 5px; 
        }
        .student-detail-field strong { 
          display: block; 
          color: #1e293b; 
          font-weight: 600; 
          overflow-wrap: anywhere; 
        }
        .student-detail-field strong.student-detail-unavailable { 
          display: inline-block; 
          padding: 4px 9px; 
          border-radius: 6px; 
          background: #fdfdfd; 
          color: #9a5b08; 
          font-size: 12px; 
          font-weight: 700; 
        }
        .student-detail-footer { 
          flex-shrink: 0; 
          padding: 12px 24px; 
          border-top: 1px solid #e2e8f0; 
          text-align: right; 
          background: #fff; 
        }
        .student-detail-footer button { 
        background: #3f5bea; 
        color: #fff; 
        border: 0; 
        border-radius: 7px; 
        padding: 8px 20px; 
        cursor: pointer; 
        }

        @media (max-width: 768px) { 
          .student-detail-overlay { 
            padding: 0; 
            align-items: flex-end; 
          } 
          .student-detail-modal { 
            height: 92vh; 
            max-height: 92vh; 
            border-radius: 16px 16px 0 0; 
          } 
          .student-detail-grid { 
            grid-template-columns: repeat(2, minmax(0, 1fr)); 
          } 
        }
          
        @media (max-width: 480px) { 
          .student-detail-grid { 
            grid-template-columns: 1fr; 
          } 
          .student-detail-header, .student-detail-body { 
            padding-left: 16px; 
            padding-right: 16px; 
          } 
        }

        .student-generate-overlay {
          position: fixed;
          inset: 0;
          z-index: 1090;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(2px);
        }

        .student-generate-modal {
          display: flex;
          flex-direction: column;
          width: min(900px, 100%);
          max-height: calc(100vh - 40px);
          overflow: hidden;
          border-radius: 18px;
          background: #ffffff;
          box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
        }

        .student-generate-header,
        .student-generate-footer {
          display: flex;
          align-items: center;
          padding: 20px 24px;
          border-color: #e8eef8;
        }

        .student-generate-header {
          justify-content: space-between;
          border-bottom: 1px solid #e8eef8;
        }

        .student-generate-header h5 {
          margin: 0 0 4px;
          color: #172554;
          font-size: 18px;
          font-weight: 800;
        }

        .student-generate-header p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
        }

        .student-generate-close,
        .student-remove-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 0;
          border-radius: 50%;
          cursor: pointer;
        }

        .student-generate-close {
          width: 36px;
          height: 36px;
          background: #f1f5f9;
          color: #475569;
          font-size: 17px;
        }

        .student-generate-body {
          flex: 1;
          overflow-y: auto;
          padding: 22px 24px;
        }

        .student-generate-filters {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }

        .student-candidate-label {
          display: block;
          color: #64748b;
          font-size: 12px;
          font-weight: 700;
        }

        .student-candidate-card {
          margin-top: 18px;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          overflow: hidden;
        }

        .student-candidate-summary {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 15px 16px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }

        .student-candidate-summary strong {
          display: block;
          margin-top: 2px;
          color: #172554;
          font-size: 18px;
        }

        .student-show-button {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border: 0;
          background: transparent;
          color: #3f5bea;
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
        }

        .student-candidate-search {
          display: flex;
          align-items: center;
          gap: 9px;
          margin: 14px 14px 0;
          padding: 0 12px;
          height: 40px;
          border: 1px solid #dce6f6;
          border-radius: 9px;
          color: #64748b;
          background: #fff;
        }

        .student-candidate-search:focus-within {
          border-color: #3f5bea;
        }

        .student-candidate-search input {
          width: 100%;
          border: 0;
          outline: 0;
          background: transparent;
          color: #1e293b;
          font-size: 13px;
        }

        .student-candidate-list {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          padding: 14px;
        }

        .student-select-all {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-bottom: 1px solid #e7edf7;
          color: #334155;
          font-size: 13px;
          cursor: pointer;
        }

        .student-select-all input,
        .student-candidate-checkbox {
          flex: 0 0 auto;
          width: 16px;
          height: 16px;
          accent-color: #3273dc;
          cursor: pointer;
        }

        .student-candidate-list.is-expanded {
          max-height: 330px;
          overflow-y: auto;
        }

        .student-candidate-item {
          display: flex;
          align-items: center;
          min-width: 0;
          gap: 10px;
          padding: 10px;
          border: 1px solid #e7edf7;
          border-radius: 12px;
          background: #ffffff;
        }

        .student-candidate-avatar,
        .student-table-avatar {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          border-radius: 50%;
          background: #dce8ff;
          color: #3156a3;
          font-weight: 800;
        }

        .student-candidate-avatar {
          width: 36px;
          height: 36px;
          font-size: 11px;
        }

        .student-candidate-info {
          flex: 1;
          min-width: 0;
        }

        .student-candidate-info strong,
        .student-candidate-info span {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .student-candidate-info strong {
          color: #1e293b;
          font-size: 12px;
        }

        .student-candidate-info span {
          margin-top: 2px;
          color: #94a3b8;
          font-size: 10px;
        }

        .student-remove-button {
          flex: 0 0 28px;
          width: 28px;
          height: 28px;
          background: #fff1f2;
          color: #e11d48;
        }

        .student-candidate-empty {
          padding: 34px 16px;
          color: #64748b;
          font-size: 13px;
          text-align: center;
        }

        .student-generate-footer {
          justify-content: flex-end;
          gap: 10px;
          border-top: 1px solid #e8eef8;
          background: #ffffff;
        }

        .student-cancel-button,
        .student-submit-button {
          min-height: 40px;
          padding: 0 18px;
          border-radius: 9px;
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
        }

        .student-cancel-button {
          border: 1px solid #cbd5e1;
          background: #ffffff;
          color: #475569;
        }

        .student-submit-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 0;
          background: #3f5bea;
          color: #ffffff;
        }

        .student-submit-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .student-table-avatar {
          width: 34px;
          height: 34px;
          overflow: hidden;
          font-size: 10px;
        }

        .student-table-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .student-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 0;
          cursor: pointer;
          padding: 5px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
        }

        .student-status-badge.is-active {
          background: #dcfce7;
          color: #15803d;
        }

        .student-status-badge.is-inactive {
          background: #fee2e2;
          color: #b91c1c;
        }
        .student-status-badge:hover { filter: brightness(.95); }
        .student-status-modal { width: min(100%, 570px); max-height: calc(100vh - 40px); overflow: auto; background: #fff; border-radius: 17px; color: #1b2948; box-shadow: 0 20px 60px #101b3440; }
        .student-status-head, .student-status-footer { display: flex; align-items: center; padding: 22px 26px; font-size: 13px; }
        .student-status-head { justify-content: space-between; border-bottom: 1px solid #e1e7f2; }
        .student-status-head h3 { margin: 0; font-size: 20px; font-weight: 800; }
        .student-status-head button { border: 0; background: none; font-size: 30px; line-height: 1; color: #243453; cursor: pointer; }
        .student-status-body { padding: 24px 26px 30px; }
        .student-status-info { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; font-size: 14px; }
        .student-status-info span, .student-status-field { font-weight: 600; }
        .student-status-info strong { font-weight: 600; }
        .student-status-switch-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 26px 18px; border: 1px solid #dbe3f4; border-radius: 13px; background: #f8faff; margin-bottom: 24px; font-size: 14px; }
        .student-status-switch-control { display: flex; align-items: center; gap: 12px; cursor: pointer; white-space: nowrap; }
        .student-status-switch-control input { position: absolute; opacity: 0; }
        .student-status-switch { width: 48px; height: 28px; border-radius: 20px; background: #dfe4ed; border: 2px solid #d2dbf3; position: relative; transition: background .2s; }
        .student-status-switch:before { content: ''; position: absolute; top: 3px; left: 3px; height: 18px; width: 18px; border-radius: 50%; background: white; box-shadow: 0 2px 5px #0003; transition: transform .2s; }
        .student-status-switch-control input:checked + .student-status-switch { background: #405bea; border-color: #405bea; }
        .student-status-switch-control input:checked + .student-status-switch:before { transform: translateX(20px); }
        .student-status-switch-control input:focus-visible + .student-status-switch { outline: 3px solid #a9b9ff; }
        .active-label { color: #15934d; }.inactive-label { color: #8490a9; }
        .student-status-field { display: flex; flex-direction: column; gap: 8px; margin-top: 22px; font-size: 14px; }
        .student-status-field em { display: inline; color: #ef4257; }
        .student-status-field input, .student-status-field textarea { width: 100%; border: 1px solid #d3def2; border-radius: 10px; padding: 11px 14px; color: #223353; background: #fff; font: inherit; }
        .student-status-field textarea { min-height: 150px; resize: vertical; }
        .student-status-footer { justify-content: flex-end; gap: 10px; border-top: 1px solid #e1e7f2; }
        .student-status-footer button { border-radius: 10px; padding: 11px 32px; font-weight: 700; cursor: pointer; border: 1px solid #d3def2; color: #405070; background: #fff; }
        .student-status-footer button[type=submit] { background: #405bea; border-color: #405bea; color: #fff; }
        .student-status-footer button:disabled { opacity: .6; cursor: not-allowed; }

        .sims-table-tab th,
        .sims-table-tab td {
          font-size: 13px !important;
        }

        .student-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 16px 18px;
          border-top: 1px solid #e7edf7;
          background: #ffffff;
          font-size: 13px;
        }

        .student-pagination-info,
        .student-pagination-info label,
        .student-pagination-controls {
          display: flex;
          align-items: center;
        }

        .student-pagination-info {
          flex-wrap: wrap;
          gap: 14px;
          color: #64748b;
        }

        .student-pagination-info label {
          gap: 7px;
          margin: 0;
        }

        .student-pagination-info select {
          height: 34px;
          min-width: 62px;
          padding: 0 8px;
          border: 1px solid #d8e1ef;
          border-radius: 7px;
          background: #ffffff;
          color: #334155;
          font-size: 13px;
          outline: none;
        }

        .student-pagination-controls {
          justify-content: flex-end;
          gap: 6px;
        }

        .student-page-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 36px;
          height: 36px;
          padding: 0 8px;
          border: 1px solid #d8e1ef;
          border-radius: 8px;
          background: #ffffff;
          color: #475569;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }

        .student-page-button:hover:not(:disabled):not(.is-active) {
          border-color: #9eb0ee;
          color: #3f5bea;
        }

        .student-page-button.is-active {
          border-color: #3f5bea;
          background: #3f5bea;
          color: #ffffff;
        }

        .student-page-button:disabled {
          border-color: #edf1f7;
          background: #f8fafc;
          color: #cbd5e1;
          cursor: not-allowed;
        }

        .student-page-nav {
          font-size: 20px;
          line-height: 1;
        }

        .student-page-gap {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          color: #94a3b8;
        }

        @media (max-width: 768px) {
          .student-generate-overlay {
            align-items: flex-end;
            padding: 0;
          }

          .student-generate-modal {
            max-height: 94vh;
            border-radius: 18px 18px 0 0;
          }

          .student-generate-header,
          .student-generate-body,
          .student-generate-footer {
            padding-left: 16px;
            padding-right: 16px;
          }

          .student-generate-filters,
          .student-candidate-list {
            grid-template-columns: 1fr;
          }

          .student-generate-footer {
            display: grid;
            grid-template-columns: 1fr 1.6fr;
          }

          .student-cancel-button,
          .student-submit-button {
            justify-content: center;
            width: 100%;
            padding: 0 10px;
          }

          .student-pagination {
            align-items: stretch;
            flex-direction: column;
          }

          .student-pagination-info {
            justify-content: space-between;
          }

          .student-pagination-controls {
            justify-content: flex-start;
            overflow-x: auto;
            padding-bottom: 2px;
          }

        }
      `}</style>
    </div>
  );
}
